import React, { forwardRef, useMemo } from "react";
import { Dropdown } from "react-bootstrap";
import { FaChevronDown, FaGlobe } from "react-icons/fa";
import { useTranslation } from "react-i18next";

/** Fixed English labels in the selector (do not translate with i18n). */
const LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "fr", label: "French" },
  { code: "nl", label: "Dutch" },
  { code: "it", label: "Italian" },
  { code: "es", label: "Spanish" },
  { code: "ar", label: "Standard Arabic" },
  { code: "zh", label: "Mandarin Chinese" },
  { code: "pt", label: "Portuguese" },
] as const;

export type LiveTranslationDropdownVariant = "on-dark" | "on-light";

type Props = {
  /** `on-dark`: white globe + label on blue (login). `on-light`: dark text on navbar. */
  variant?: LiveTranslationDropdownVariant;
};

/** Normalizes i18n language to a supported UI code (same set as the navbar selector). */
export function normalizeLiveLanguageCode(
  lng: string | undefined,
  fallback = "en"
): string {
  const raw = (lng || fallback).split("-")[0]?.toLowerCase() ?? fallback;
  const supported = new Set<string>(LANGUAGE_OPTIONS.map((o) => o.code));
  return supported.has(raw) ? raw : fallback;
}

/** Plain button toggle — no Bootstrap caret (we use FaChevronDown). */
const LanguageToggle = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function LanguageToggle({ children, className, onClick, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={className}
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
      {...rest}
    >
      {children}
    </button>
  );
});

const LiveTranslationDropdown: React.FC<Props> = ({ variant = "on-light" }) => {
  const { i18n } = useTranslation();
  const currentLanguage = normalizeLiveLanguageCode(i18n.language, "en");

  const currentLabel = useMemo(() => {
    return (
      LANGUAGE_OPTIONS.find((o) => o.code === currentLanguage)?.label ?? "English"
    );
  }, [currentLanguage]);

  const triggerClass =
    variant === "on-dark"
      ? "text-white"
      : "text-dark";

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        as={LanguageToggle}
        id="live-language-dropdown"
        aria-label={`Language: ${currentLabel}`}
        className={`d-inline-flex align-items-center gap-2 p-0 border-0 bg-transparent shadow-none text-decoration-none fw-medium ${triggerClass}`}
      >
        <FaGlobe aria-hidden />
        <span>{currentLabel}</span>
        <FaChevronDown size={12} aria-hidden />
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {LANGUAGE_OPTIONS.map(({ code, label }) => (
          <Dropdown.Item
            key={code}
            active={code === currentLanguage}
            onClick={() => void i18n.changeLanguage(code)}
          >
            {label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default LiveTranslationDropdown;
